<template>
    <div>
        <div v-if="viewer" class="card border-0 shadow-sm">
            <div
                class="card-header bg-white d-flex align-items-center justify-content-between gap-2"
            >
                <span class="fw-semibold">{{ viewer.title }}</span>
                <div class="d-flex gap-2">
                    <button
                        v-if="viewer.type === 'html' && !viewer.loading"
                        class="btn btn-sm btn-outline-danger"
                        :disabled="mengunduh_pdf"
                        @click="unduhPdf"
                    >
                        <i
                            class="fa"
                            :class="
                                mengunduh_pdf
                                    ? 'fa-spinner fa-spin'
                                    : 'fa-file-pdf-o'
                            "
                        ></i>
                        {{ mengunduh_pdf ? "Menyiapkan..." : "Export PDF" }}
                    </button>
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        @click="viewer = null"
                    >
                        <i class="fa fa-arrow-left"></i> Kembali
                    </button>
                </div>
            </div>

            <div class="card-body">
                <div v-if="viewer.loading" class="text-center py-5">
                    <div
                        class="spinner-border text-success"
                        role="status"
                    ></div>
                    <div class="mt-2 fw-semibold text-muted">Memuat isi...</div>
                </div>

                <iframe
                    v-else-if="viewer.type === 'html'"
                    :srcdoc="viewer.html"
                    class="snapshot-frame"
                    frameborder="0"
                ></iframe>

                <div v-else-if="viewer.type === 'lab'">
                    <div class="row g-2 mb-3 small">
                        <div class="col-md-4">
                            <span class="text-muted">No. Lab</span><br />
                            <span class="fw-semibold">{{
                                viewer.head.Nolab || "-"
                            }}</span>
                        </div>
                        <div class="col-md-4">
                            <span class="text-muted">Tanggal Hasil</span><br />
                            <span class="fw-semibold">{{
                                formatTanggal(viewer.head.Tglhasil)
                            }}</span>
                        </div>
                        <div class="col-md-4">
                            <span class="text-muted">Dokter Pengirim</span
                            ><br />
                            <span class="fw-semibold">{{
                                viewer.head.NmDoc || "-"
                            }}</span>
                        </div>
                    </div>

                    <div
                        v-for="(grup, nama_grup) in viewer.groups"
                        :key="nama_grup"
                        class="mb-3"
                    >
                        <div
                            class="fw-bold text-success border-bottom pb-1 mb-2"
                        >
                            {{ nama_grup }}
                        </div>

                        <div class="table-responsive">
                            <table
                                class="table table-sm table-striped align-middle mb-0"
                            >
                                <thead class="table-light">
                                    <tr>
                                        <th>Pemeriksaan</th>
                                        <th style="width: 140px">Hasil</th>
                                        <th style="width: 100px">Satuan</th>
                                        <th style="width: 180px">
                                            Nilai Normal
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(item, kd_detail) in grup">
                                        <tr :key="`head-${kd_detail}`">
                                            <td class="fw-semibold">
                                                {{ item.head.NMDetail }}
                                            </td>
                                            <td
                                                :class="{
                                                    'text-danger fw-bold':
                                                        isKritis(item.head),
                                                }"
                                            >
                                                {{ item.head.Hasil || "-" }}
                                            </td>
                                            <td>
                                                {{ item.head.Satuan || "-" }}
                                            </td>
                                            <td>
                                                {{ nilaiNormal(item.head) }}
                                            </td>
                                        </tr>
                                        <tr
                                            v-for="detail in item.detail || []"
                                            :key="`detail-${detail.KDDetail}`"
                                        >
                                            <td class="ps-4 text-muted">
                                                — {{ detail.NMDetail }}
                                            </td>
                                            <td
                                                :class="{
                                                    'text-danger fw-bold':
                                                        isKritis(detail),
                                                }"
                                            >
                                                {{ detail.Hasil || "-" }}
                                            </td>
                                            <td>{{ detail.Satuan || "-" }}</td>
                                            <td>{{ nilaiNormal(detail) }}</td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <p
                        v-if="!Object.keys(viewer.groups).length"
                        class="text-muted mb-0"
                    >
                        Tidak ada detail pemeriksaan pada transaksi ini.
                    </p>
                </div>

                <div v-else-if="viewer.type === 'cppt'">
                    <div v-if="cpptLoading" class="text-muted py-3">
                        <i class="fa fa-spinner fa-spin me-1"></i> Memuat
                        CPPT...
                    </div>

                    <p v-else-if="cpptError" class="text-danger mb-0">
                        <i class="fa fa-exclamation-triangle me-1"></i> CPPT
                        gagal diambil dari api-simrs.
                    </p>

                    <template v-else-if="cpptRows.length">
                        <div class="table-responsive">
                            <table
                                class="table table-bordered align-middle mb-0 cppt-table"
                            >
                                <thead class="table-light text-center">
                                    <tr>
                                        <th style="width: 130px">
                                            Tanggal dan Jam Pemeriksaan
                                        </th>
                                        <th colspan="2">Uraian Pemeriksaan</th>
                                        <th style="width: 200px">
                                            Instruksi DPJP
                                        </th>
                                        <th style="width: 170px">
                                            Nama &amp; Tanda Tangan DPJP /
                                            Tenaga Kesehatan
                                        </th>
                                        <th style="width: 110px">Unit</th>
                                    </tr>
                                </thead>

                                <tbody
                                    v-for="row in cpptRows"
                                    :key="row.IdCPPT"
                                >
                                    <tr
                                        v-for="(soap, index) in uraianCppt(row)"
                                        :key="`${row.IdCPPT}-${soap.kode}`"
                                    >
                                        <td
                                            v-if="index === 0"
                                            :rowspan="4"
                                            class="text-center"
                                        >
                                            <div>
                                                {{
                                                    formatTanggalCppt(
                                                        row.CreatedAt
                                                    )
                                                }}
                                            </div>
                                            <div class="small text-muted">
                                                {{
                                                    formatJamCppt(row.CreatedAt)
                                                }}
                                            </div>
                                        </td>

                                        <td
                                            class="text-center fw-bold soap-kode"
                                        >
                                            {{ soap.kode }}
                                        </td>
                                        <td class="pre-wrap">
                                            {{ soap.isi || "" }}
                                        </td>

                                        <td
                                            v-if="index === 0"
                                            :rowspan="4"
                                            class="pre-wrap align-top small"
                                        >
                                            {{ instruksiCppt(row) || "" }}
                                        </td>

                                        <td
                                            v-if="index === 0"
                                            :rowspan="4"
                                            class="align-top small"
                                        >
                                            <div
                                                v-if="row.TandaTangan"
                                                class="mb-1 text-center"
                                            >
                                                <img
                                                    :src="
                                                        '/storage/signatures/' +
                                                        row.TandaTangan
                                                    "
                                                    alt="Tanda Tangan"
                                                    style="
                                                        max-width: 100px;
                                                        max-height: 50px;
                                                    "
                                                />
                                            </div>
                                            <div class="fw-semibold">
                                                {{
                                                    row.SavedBy ||
                                                    row.name ||
                                                    "-"
                                                }}
                                            </div>
                                            <div class="text-muted">
                                                {{
                                                    formatTanggalCppt(
                                                        row.CreatedAt
                                                    )
                                                }}
                                            </div>
                                            <div
                                                v-if="row.DiisiOleh"
                                                class="badge bg-light text-dark border mt-1"
                                            >
                                                {{ row.DiisiOleh }}
                                            </div>
                                            <div
                                                v-if="row.ApproveCPPT"
                                                class="text-success small mt-1"
                                            >
                                                <i
                                                    class="fa fa-check-circle"
                                                ></i>
                                                Read back
                                                {{
                                                    formatTanggalCppt(
                                                        row.ApproveCPPTDate
                                                    )
                                                }}
                                            </div>
                                        </td>

                                        <td
                                            v-if="index === 0"
                                            :rowspan="4"
                                            class="align-top small"
                                        >
                                            <div>{{ unitCppt(row) }}</div>
                                            <div
                                                v-if="row.Poli"
                                                class="text-muted"
                                            >
                                                {{ row.Poli }}
                                            </div>
                                            <div
                                                v-if="row.Regno"
                                                class="text-muted"
                                            >
                                                No. Reg {{ row.Regno }}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div
                            class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2"
                        >
                            <span class="text-muted small">
                                Halaman {{ cpptPage }} —
                                {{ cpptRows.length }} dari
                                {{ cpptCount }} catatan
                            </span>
                            <div class="btn-group btn-group-sm">
                                <button
                                    class="btn btn-outline-secondary"
                                    :disabled="!cpptPrev"
                                    @click="pindahHalamanCppt(cpptPrev)"
                                >
                                    <i class="fa fa-chevron-left"></i>
                                    Sebelumnya
                                </button>
                                <button
                                    class="btn btn-outline-secondary"
                                    :disabled="!cpptNext"
                                    @click="pindahHalamanCppt(cpptNext)"
                                >
                                    Berikutnya
                                    <i class="fa fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    </template>

                    <p v-else class="text-muted mb-0">
                        <i class="fa fa-inbox me-1"></i>
                        Tidak ada CPPT {{ labelCppt }} untuk pasien ini.
                    </p>
                </div>

                <div v-else-if="viewer.type === 'obat'">
                    <div v-if="obatLoading" class="text-muted py-3">
                        <i class="fa fa-spinner fa-spin me-1"></i> Memuat data
                        obat...
                    </div>

                    <p v-else-if="obatError" class="text-danger mb-0">
                        <i class="fa fa-exclamation-triangle me-1"></i> Data
                        obat gagal diambil dari api-simrs.
                    </p>

                    <template v-else-if="viewer.jenis === 'elektronik'">
                        <div v-if="obatList.length">
                            <div class="row g-3 small mb-3 border-bottom pb-3">
                                <div
                                    v-for="item in identitasObat"
                                    :key="item.label"
                                    class="col-md-6 col-lg-4"
                                >
                                    <div class="text-muted">
                                        {{ item.label }}
                                    </div>
                                    <div class="fw-semibold">
                                        {{ item.value || "-" }}
                                    </div>
                                </div>
                            </div>

                            <div v-for="r in obatList" :key="r.id" class="mb-4">
                                <div
                                    class="d-flex flex-wrap justify-content-between gap-2 mb-2"
                                >
                                    <span class="badge bg-success"
                                        >Resep {{ r.id }}</span
                                    >
                                    <span class="text-muted small">
                                        {{ r.dokter || "-" }} —
                                        {{
                                            formatTanggalJamObat(
                                                r.jam || r.tanggal
                                            )
                                        }}
                                    </span>
                                </div>

                                <div class="border rounded p-3 bg-white">
                                    <div
                                        v-for="obat in r.non_racikan"
                                        :key="obat.kode_obat"
                                        class="resep-baris"
                                    >
                                        <div class="resep-r">R/</div>
                                        <div>
                                            <div class="fw-semibold">
                                                {{ obat.nama_obat }}
                                            </div>
                                            <div class="text-muted small">
                                                S
                                                {{
                                                    obat.signa_manual ||
                                                    obat.signa
                                                }}
                                                <template
                                                    v-if="obat.jumlah_hari"
                                                >
                                                    —
                                                    {{ obat.jumlah_hari }} hari
                                                </template>
                                            </div>
                                        </div>
                                        <div class="text-nowrap">
                                            No. {{ obat.qty }}
                                        </div>
                                    </div>

                                    <div
                                        v-for="rd in r.racikan_dokter"
                                        :key="`rd-${rd.no_racikan}`"
                                        class="resep-baris"
                                    >
                                        <div class="resep-r">R/</div>
                                        <div>
                                            <div class="fw-semibold">
                                                Racikan ke-{{ rd.no_racikan }}
                                            </div>
                                            <div class="text-muted small">
                                                S {{ rd.signa1 }} dd
                                                {{ rd.signa2 }}
                                            </div>
                                            <ul class="mb-0 ps-3 small">
                                                <li
                                                    v-for="obat in rd.obat"
                                                    :key="obat.kode_obat"
                                                >
                                                    {{ obat.nama_obat }}
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="text-nowrap">
                                            No. {{ rd.qty }}
                                        </div>
                                    </div>

                                    <div
                                        v-for="(rc, index) in r.racikan"
                                        :key="`rc-${index}`"
                                        class="resep-baris"
                                    >
                                        <div class="resep-r">R/</div>
                                        <div>
                                            <div class="fw-semibold">
                                                {{ rc.Racikan }}
                                            </div>
                                            <div class="text-muted small">
                                                S {{ rc.Signa || "-" }}
                                            </div>
                                        </div>
                                        <div class="text-nowrap">
                                            No. {{ rc.Catatan }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <p v-else class="text-muted mb-0">
                            <i class="fa fa-inbox me-1"></i> Belum ada resep
                            elektronik pada registrasi ini.
                        </p>
                    </template>

                    <template v-else-if="obatList.length">
                        <div
                            v-for="r in obatList"
                            :key="r.id"
                            class="card border mb-3"
                        >
                            <div
                                class="card-header bg-light d-flex flex-wrap align-items-center gap-2 py-2"
                            >
                                <span class="badge bg-success"
                                    >Resep ke-{{ r.resep_ke }}</span
                                >
                                <span class="fw-semibold small">{{
                                    waktuResep(r)
                                }}</span>
                                <span v-if="r.poli" class="text-muted small">{{
                                    r.poli
                                }}</span>
                                <span class="text-muted small ms-auto">
                                    {{ r.dokter || "-" }}
                                    <template v-if="r.regno"
                                        >— No. Reg {{ r.regno }}</template
                                    >
                                </span>
                            </div>

                            <div class="table-responsive">
                                <table class="table table-sm align-middle mb-0">
                                    <thead class="table-light">
                                        <tr>
                                            <th style="width: 110px">Kode</th>
                                            <th>Nama Obat</th>
                                            <th style="width: 80px">Qty</th>
                                            <th style="width: 170px">Signa</th>
                                            <th>Keterangan</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="obat in r.obat"
                                            :key="`${r.id}-${obat.kode_obat}-${obat.no_racikan}`"
                                        >
                                            <td class="text-muted">
                                                {{ obat.kode_obat }}
                                            </td>
                                            <td>
                                                {{ obat.nama_obat }}
                                                <span
                                                    v-if="obat.jenis === 4"
                                                    class="badge bg-secondary ms-1"
                                                >
                                                    Racikan ke-{{
                                                        obat.no_racikan
                                                    }}
                                                </span>
                                            </td>
                                            <td>
                                                {{ obat.qty }} {{ obat.satuan }}
                                            </td>
                                            <td>
                                                {{
                                                    obat.signa_manual ||
                                                    obat.signa
                                                }}
                                            </td>
                                            <td class="small text-muted">
                                                {{
                                                    obat.keterangan_pakai ||
                                                    obat.cara_minum ||
                                                    ""
                                                }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div
                                v-if="(r.racikan || []).length"
                                class="table-responsive border-top"
                            >
                                <table class="table table-sm align-middle mb-0">
                                    <thead class="table-light">
                                        <tr>
                                            <th>Racikan</th>
                                            <th style="width: 170px">Signa</th>
                                            <th style="width: 80px">Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(rc, index) in r.racikan"
                                            :key="`${r.id}-rc-${index}`"
                                        >
                                            <td>{{ rc.Racikan }}</td>
                                            <td>{{ rc.Signa }}</td>
                                            <td>{{ rc.Catatan }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </template>

                    <p v-else class="text-muted mb-0">
                        <i class="fa fa-inbox me-1"></i> Tidak ada
                        {{ labelObat }} untuk pasien ini.
                    </p>
                </div>

                <div v-else-if="viewer.type === 'rad'">
                    <div
                        v-for="(row, index) in viewer.rows"
                        :key="index"
                        class="mb-4"
                    >
                        <div class="row g-2 mb-2 small">
                            <div class="col-md-4">
                                <span class="text-muted">Pemeriksaan</span
                                ><br />
                                <span class="fw-semibold">{{
                                    row.NmTarif || "-"
                                }}</span>
                            </div>
                            <div class="col-md-4">
                                <span class="text-muted">Tanggal Hasil</span
                                ><br />
                                <span class="fw-semibold">{{
                                    formatTanggal(row.TglHasil)
                                }}</span>
                            </div>
                            <div class="col-md-4">
                                <span class="text-muted">Dokter Pembaca</span
                                ><br />
                                <span class="fw-semibold">{{
                                    row.DokterPembaca || "-"
                                }}</span>
                            </div>
                        </div>

                        <div
                            class="border rounded p-3 bg-white hasil-rad"
                            v-html="row.Hasil"
                        ></div>

                        <div
                            v-if="
                                index === 0 &&
                                row.photo_url &&
                                row.photo_url.photo_url
                            "
                            class="mt-2 d-flex flex-wrap gap-2"
                        >
                            <a
                                :href="row.photo_url.photo_url"
                                target="_blank"
                                class="btn btn-sm btn-outline-primary"
                            >
                                <i class="fa fa-eye"></i> Lihat Citra
                            </a>
                            <a
                                v-if="row.photo_url.photo_url_vol_render"
                                :href="row.photo_url.photo_url_vol_render"
                                target="_blank"
                                class="btn btn-sm btn-outline-primary"
                            >
                                <i class="fa fa-cube"></i> Volume Render
                            </a>
                        </div>

                        <div
                            v-if="(row.foto || []).length"
                            class="mt-2 d-flex flex-wrap gap-2"
                        >
                            <figure
                                v-for="foto in row.foto"
                                :key="foto.HasilRadiologiFotoId"
                                class="mb-0 text-center"
                            >
                                <img
                                    v-if="fotoSrc(foto)"
                                    :src="fotoSrc(foto)"
                                    class="img-thumbnail rad-foto"
                                    :alt="foto.Keterangan || 'Foto radiologi'"
                                />
                                <figcaption class="small text-muted">
                                    {{ foto.Keterangan || "-" }}
                                </figcaption>
                            </figure>
                        </div>
                    </div>

                    <p v-if="!viewer.rows.length" class="text-muted mb-0">
                        Hasil radiologi tidak ditemukan.
                    </p>
                </div>
            </div>
        </div>

        <div v-else class="accordion" id="accordion-riwayat-icd">
            <div
                v-for="section in sections"
                :key="section.key"
                class="accordion-item"
            >
                <h2 class="accordion-header">
                    <button
                        class="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        :data-bs-target="`#collapse-icd-${section.key}`"
                        @click="loadSection(section.key)"
                    >
                        <i
                            class="fa me-2 text-success"
                            :class="section.icon"
                        ></i>
                        {{ section.title }}
                        <span
                            v-if="counts[section.key] !== null"
                            class="badge bg-secondary ms-2"
                        >
                            {{ counts[section.key] }}
                        </span>
                    </button>
                </h2>

                <div
                    :id="`collapse-icd-${section.key}`"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordion-riwayat-icd"
                >
                    <div class="accordion-body">
                        <ul
                            v-if="menu[section.kind]"
                            class="list-unstyled mb-0"
                        >
                            <li
                                v-for="jenis in menu[section.kind]"
                                :key="jenis.value"
                                class="py-1 border-bottom"
                            >
                                <a
                                    href="#"
                                    @click.prevent="openMenu(section, jenis)"
                                >
                                    <i class="fa me-1" :class="jenis.icon"></i>
                                    {{ jenis.label }}
                                </a>
                            </li>
                        </ul>

                        <div
                            v-else-if="loading[section.key]"
                            class="text-muted"
                        >
                            <i class="fa fa-spinner fa-spin me-1"></i> Memuat
                            data...
                        </div>

                        <p
                            v-else-if="errors[section.key]"
                            class="text-danger mb-0"
                        >
                            <i class="fa fa-exclamation-triangle me-1"></i>
                            Data gagal diambil dari api-simrs.
                        </p>

                        <template v-else-if="(items[section.key] || []).length">
                            <ul
                                v-if="section.kind === 'form'"
                                class="list-unstyled mb-0"
                            >
                                <li
                                    v-for="row in items[section.key]"
                                    :key="`${row.form_id}-${row.id}`"
                                    class="py-1 border-bottom"
                                >
                                    <a href="#" @click.prevent="openForm(row)">
                                        <i class="fa fa-file-text-o me-1"></i>
                                        {{ row.head_name }}
                                    </a>
                                    <span class="text-muted small ms-2">
                                        {{ formatTanggalJam(row.created_at) }}
                                    </span>
                                </li>
                            </ul>

                            <ul
                                v-else-if="section.kind === 'lab'"
                                class="list-unstyled mb-0"
                            >
                                <li
                                    v-for="row in items[section.key]"
                                    :key="row.Notran"
                                    class="py-1 border-bottom"
                                >
                                    <a href="#" @click.prevent="openLab(row)">
                                        <i class="fa fa-flask me-1"></i>
                                        {{ row.date }}
                                    </a>
                                </li>
                            </ul>

                            <ul
                                v-else-if="section.kind === 'rad'"
                                class="list-unstyled mb-0"
                            >
                                <li
                                    v-for="(row, index) in items[section.key]"
                                    :key="`${row.NoTran}-${index}`"
                                    class="py-1 border-bottom"
                                >
                                    <a href="#" @click.prevent="openRad(row)">
                                        <i class="fa fa-photo me-1"></i>
                                        {{ row.date }} —
                                        {{ row.NmTarif || "-" }}
                                    </a>
                                </li>
                            </ul>

                            <ul
                                v-else-if="section.kind === 'dokumen'"
                                class="list-unstyled mb-0"
                            >
                                <li
                                    v-for="(row, index) in items[section.key]"
                                    :key="index"
                                    class="py-1 border-bottom d-flex justify-content-between gap-2"
                                >
                                    <a
                                        :href="dokumenUrl(row.Fileurl)"
                                        target="_blank"
                                    >
                                        <i class="fa fa-paperclip me-1"></i>
                                        {{ row.Filename }}
                                    </a>
                                    <span class="text-muted small">
                                        {{ formatTanggal(row.Uploaddate) }}
                                        <template v-if="row.UploadBy"
                                            >— {{ row.UploadBy }}</template
                                        >
                                    </span>
                                </li>
                            </ul>

                            <div v-else>
                                <div
                                    v-for="berkas in items[section.key]"
                                    :key="berkas.id"
                                    class="mb-3 pb-3 border-bottom"
                                >
                                    <div
                                        class="d-flex flex-wrap align-items-baseline gap-2 mb-2"
                                    >
                                        <span class="badge bg-success">
                                            {{ berkas.doc_title || "-" }}
                                        </span>
                                        <span class="fw-semibold">
                                            {{ berkas.doc_description || "-" }}
                                        </span>
                                        <span class="text-muted small ms-auto">
                                            {{
                                                formatTanggal(berkas.created_at)
                                            }}
                                            <template v-if="berkas.created_by">
                                                — {{ berkas.created_by }}
                                            </template>
                                        </span>
                                    </div>

                                    <div class="d-flex flex-wrap gap-2">
                                        <button
                                            v-for="(file, index) in fileBerkas(
                                                berkas
                                            )"
                                            :key="file.key"
                                            type="button"
                                            class="btn btn-light border p-1 berkas-thumb"
                                            :title="`Klik untuk memperbesar — ${file.label}`"
                                            @click="openBerkas(berkas, index)"
                                        >
                                            <img
                                                :src="file.url"
                                                :alt="file.label"
                                            />
                                            <span
                                                class="d-block small text-muted mt-1"
                                            >
                                                {{ file.label }}
                                            </span>
                                        </button>
                                    </div>

                                    <p
                                        v-if="!fileBerkas(berkas).length"
                                        class="text-muted small mb-0"
                                    >
                                        Tidak ada file pada berkas ini.
                                    </p>
                                </div>
                            </div>
                        </template>

                        <p v-else class="text-muted mb-0">
                            <i class="fa fa-inbox me-1"></i> Tidak ada data yang
                            ditampilkan.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="preview" class="berkas-preview" @click.self="closeBerkas">
            <div class="berkas-preview-bar">
                <span class="text-truncate">
                    {{ preview.title }}
                    <span class="opacity-75">
                        — {{ preview.index + 1 }} / {{ preview.files.length }}
                    </span>
                </span>
                <div class="d-flex gap-2">
                    <a
                        :href="preview.files[preview.index].url"
                        target="_blank"
                        class="btn btn-sm btn-outline-light"
                    >
                        <i class="fa fa-external-link"></i> Buka Tab Baru
                    </a>
                    <button
                        class="btn btn-sm btn-outline-light"
                        @click="closeBerkas"
                    >
                        <i class="fa fa-times"></i> Tutup
                    </button>
                </div>
            </div>

            <button
                v-if="preview.files.length > 1"
                class="berkas-nav start-0"
                @click.stop="geserBerkas(-1)"
                aria-label="Sebelumnya"
            >
                <i class="fa fa-chevron-left"></i>
            </button>

            <img
                :src="preview.files[preview.index].url"
                :alt="preview.files[preview.index].label"
                class="berkas-preview-img"
            />

            <button
                v-if="preview.files.length > 1"
                class="berkas-nav end-0"
                @click.stop="geserBerkas(1)"
                aria-label="Berikutnya"
            >
                <i class="fa fa-chevron-right"></i>
            </button>
        </div>
    </div>
</template>

<script>
const SECTIONS = [
    {
        key: "resume_medis",
        title: "Resume Medis",
        icon: "fa-file-text-o",
        kind: "cppt",
    },
    {
        key: "obat",
        title: "Riwayat Obat Pasien",
        icon: "fa-medkit",
        kind: "obat",
    },
    {
        key: "pemeriksaan",
        title: "Riwayat Pemeriksaan",
        icon: "fa-stethoscope",
        kind: "form",
    },
    {
        key: "lab",
        title: "Riwayat Laboratorium",
        icon: "fa-flask",
        kind: "lab",
    },
    {
        key: "radiologi",
        title: "Riwayat Radiologi",
        icon: "fa-photo",
        kind: "rad",
    },
    {
        key: "triase",
        title: "Riwayat Triase",
        icon: "fa-ambulance",
        kind: "form",
    },
    {
        key: "dokumen",
        title: "Dokumen Pasien",
        icon: "fa-folder-open-o",
        kind: "dokumen",
    },
    {
        key: "rekam_medis",
        title: "Status Rekam Medis",
        icon: "fa-archive",
        kind: "rekam_medis",
    },
];

const FORM_ID_TRIASE = 112;

const JENIS_CPPT = [
    { value: "rajal", label: "CPPT Rawat Jalan", icon: "fa-stethoscope" },
    { value: "ranap", label: "CPPT Rawat Inap", icon: "fa-bed" },
    { value: "semua", label: "CPPT Semua Unit", icon: "fa-list" },
];

const JENIS_OBAT = [
    {
        value: "resep",
        label: "Riwayat Resep Dokter",
        icon: "fa-pencil-square-o",
    },
    { value: "apotik", label: "Riwayat Apotik", icon: "fa-shopping-basket" },
    {
        value: "transaksi",
        label: "Riwayat Transaksi Obat",
        icon: "fa-list-alt",
    },
    {
        value: "elektronik",
        label: "Resep Elektronik (registrasi ini)",
        icon: "fa-file-text-o",
    },
];

const ENDPOINT_OBAT = {
    resep: ["/api/riwayat-pasien/obat/resep", "riwayat resep dokter"],
    apotik: ["/api/riwayat-pasien/obat/apotik", "riwayat apotik"],
    transaksi: ["/api/riwayat-pasien/obat/transaksi", "riwayat transaksi obat"],
    elektronik: [
        "/api/riwayat-pasien/obat/resep-elektronik",
        "resep elektronik",
    ],
};

export default {
    name: "RiwayatPelayananIcd",

    props: {
        regno: {
            type: [String, Number],
            required: true,
        },
        medrec: {
            type: [String, Number],
            default: null,
        },
    },

    data() {
        const blank = (value) =>
            SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: value }), {});

        return {
            sections: SECTIONS,
            register: null,
            items: blank(null),
            loading: blank(false),
            errors: blank(false),
            counts: blank(null),
            menu: { cppt: JENIS_CPPT, obat: JENIS_OBAT },
            viewer: null,
            preview: null,
            mengunduh_pdf: false,
            cpptRows: [],
            cpptCount: 0,
            cpptPage: 1,
            cpptNext: false,
            cpptPrev: false,
            cpptLoading: false,
            cpptError: false,
            obatList: [],
            obatHead: {},
            obatLoading: false,
            obatError: false,
        };
    },

    computed: {
        regnoAktif() {
            return String(this.regno || "").trim();
        },

        medrecAktif() {
            const dariRegister =
                this.register && this.register.Medrec
                    ? String(this.register.Medrec).trim()
                    : "";

            return dariRegister || String(this.medrec || "").trim();
        },

        labelCppt() {
            const jenis = this.viewer ? this.viewer.jenis : "rajal";

            return { ranap: "rawat inap", semua: "", rajal: "rawat jalan" }[
                jenis
            ];
        },

        labelObat() {
            const jenis = this.viewer ? this.viewer.jenis : "resep";

            return ENDPOINT_OBAT[jenis] ? ENDPOINT_OBAT[jenis][1] : "";
        },

        identitasObat() {
            const head = this.obatHead || {};

            return [
                { label: "Nama Pasien", value: head.Firstname },
                { label: "No. RM", value: head.Medrec },
                { label: "No. Registrasi", value: head.Regno },
                {
                    label: "Tanggal Lahir / Usia",
                    value: [this.formatTanggalObat(head.Bod), head.usia]
                        .filter(Boolean)
                        .join(" / "),
                },
                { label: "Poli", value: head.NMPoli },
                { label: "DPJP", value: head.NmDoc },
                { label: "No. SEP", value: head.NoSep },
                { label: "Alamat", value: head.Address },
            ];
        },
    },

    watch: {
        regno: {
            immediate: true,
            handler: "getRegister",
        },
    },

    mounted() {
        document.addEventListener("keydown", this.onKeydownPreview);
    },

    beforeDestroy() {
        document.removeEventListener("keydown", this.onKeydownPreview);
    },

    methods: {
        getRegister() {
            this.register = null;

            this.axios
                .get(`/api/riwayat-pasien/register/${this.regnoAktif}`)
                .then(({ data }) => (this.register = data.data || null))
                .catch(() => (this.register = null));
        },

        loadSection(key) {
            const section = SECTIONS.find((s) => s.key === key);
            if (this.menu[section.kind]) return;

            if (
                this.items[key] !== null ||
                this.loading[key] ||
                !this.medrecAktif
            )
                return;

            const requests = {
                pemeriksaan: [
                    "/api/riwayat-pasien/pemeriksaan",
                    { medrec: this.medrecAktif },
                ],
                triase: [
                    "/api/riwayat-pasien/pemeriksaan",
                    { medrec: this.medrecAktif, form_id: FORM_ID_TRIASE },
                ],
                lab: ["/api/riwayat-pasien/lab", { medrec: this.medrecAktif }],
                radiologi: [
                    "/api/riwayat-pasien/radiologi",
                    { medrec: this.medrecAktif },
                ],
                dokumen: [
                    "/api/riwayat-pasien/dokumen",
                    { medrec: this.medrecAktif },
                ],
                rekam_medis: [
                    "/api/riwayat-pasien/rekam-medis",
                    { medrec: this.medrecAktif },
                ],
            };

            const [url, params] = requests[key];
            this.$set(this.loading, key, true);
            this.$set(this.errors, key, false);

            this.axios
                .get(url, { params })
                .then(({ data }) => {
                    const payload = data.data || {};
                    const rows = Array.isArray(payload)
                        ? payload
                        : payload.data || [];

                    this.$set(this.items, key, rows);
                    this.$set(this.counts, key, rows.length);
                })
                .catch(() => {
                    this.$set(this.items, key, null);
                    this.$set(this.counts, key, null);
                    this.$set(this.errors, key, true);
                })
                .finally(() => this.$set(this.loading, key, false));
        },

        openMenu(section, jenis) {
            this.viewer = {
                type: section.kind,
                title: jenis.label,
                loading: false,
                jenis: jenis.value,
            };

            if (section.kind === "cppt") {
                this.cpptPage = 1;
                this.getCppt();
            }

            if (section.kind === "obat") {
                this.getObat();
            }
        },

        getCppt() {
            this.cpptLoading = true;
            this.cpptError = false;

            this.axios
                .get("/api/riwayat-pasien/cppt", {
                    params: {
                        medrec: this.medrecAktif,
                        regno: this.regnoAktif,
                        jenis: this.viewer.jenis,
                        page: this.cpptPage,
                    },
                })
                .then(({ data }) => {
                    const cppt = (data.data || {}).cppt || {};

                    this.cpptRows = cppt.data || [];
                    this.cpptCount = cppt.count || 0;
                    this.cpptNext = cppt.next_page || false;
                    this.cpptPrev = cppt.previous_page || false;
                })
                .catch(() => {
                    this.cpptRows = [];
                    this.cpptCount = 0;
                    this.cpptNext = false;
                    this.cpptPrev = false;
                    this.cpptError = true;
                })
                .finally(() => (this.cpptLoading = false));
        },

        pindahHalamanCppt(page) {
            this.cpptPage = page;
            this.getCppt();
        },

        getObat() {
            const jenis = this.viewer.jenis;

            this.obatLoading = true;
            this.obatError = false;
            this.obatList = [];
            this.obatHead = {};

            this.axios
                .get(ENDPOINT_OBAT[jenis][0], {
                    params: {
                        medrec: this.medrecAktif,
                        regno: jenis === "elektronik" ? this.regnoAktif : null,
                    },
                })
                .then(({ data }) => {
                    const payload = data.data || {};

                    this.obatList = Array.isArray(payload)
                        ? payload
                        : payload.resep || [];
                    this.obatHead = payload.head || {};
                })
                .catch(() => {
                    this.obatList = [];
                    this.obatHead = {};
                    this.obatError = true;
                })
                .finally(() => (this.obatLoading = false));
        },

        uraianCppt(row) {
            return [
                { kode: "S", isi: row.Subjective },
                { kode: "O", isi: row.Objective },
                { kode: "A", isi: row.Assessment },
                { kode: "P", isi: row.Planning },
            ];
        },

        instruksiCppt(row) {
            return [
                row.Penunjang,
                row.IntuksiTendakaKesehatan,
                row.instruksi_dokter_spesialis,
                row.Konsul,
            ]
                .map((bagian) => (bagian || "").trim())
                .filter(Boolean)
                .join("\n");
        },

        unitCppt(row) {
            if (row.JenisPasien === "RI") return "Rawat Inap";
            if (row.JenisPasien === "RJ") return "Rawat Jalan";

            return row.Instalasi == 2 ? "Rawat Inap" : "Rawat Jalan";
        },

        openForm(row) {
            this.viewer = {
                type: "html",
                title: row.head_name,
                loading: true,
                html: "",
                form_id: row.form_id,
                data_id: row.id,
            };

            this.axios
                .get("/api/riwayat-pasien/form-snapshot", {
                    params: {
                        form_id: row.form_id,
                        regno: this.regnoAktif,
                        data_id: row.id,
                    },
                })
                .then(({ data }) => {
                    this.viewer.html = data.data || "";
                    this.viewer.loading = false;
                })
                .catch(() => {
                    this.viewer = null;
                    this.$swal({
                        icon: "warning",
                        title: "Isi form tidak tersedia",
                        text: "Data form untuk registrasi ini tidak ditemukan.",
                    });
                });
        },

        unduhPdf() {
            this.mengunduh_pdf = true;

            this.axios
                .get("/api/riwayat-pasien/form-pdf", {
                    params: {
                        form_id: this.viewer.form_id,
                        regno: this.regnoAktif,
                        data_id: this.viewer.data_id,
                    },
                    responseType: "blob",
                })
                .then(({ data }) => {
                    const url = URL.createObjectURL(data);
                    const tautan = document.createElement("a");

                    tautan.href = url;
                    tautan.download = `${this.viewer.title} - ${this.regnoAktif}.pdf`;
                    tautan.click();

                    URL.revokeObjectURL(url);
                })
                .catch(() =>
                    this.$swal({
                        icon: "warning",
                        title: "Gagal membuat PDF",
                        text: "Form ini tidak bisa dicetak untuk saat ini.",
                    })
                )
                .finally(() => (this.mengunduh_pdf = false));
        },

        openLab(row) {
            this.viewer = {
                type: "lab",
                title: row.date,
                loading: true,
                head: {},
                groups: {},
            };

            this.axios
                .get("/api/riwayat-pasien/lab-detail", {
                    params: { notrans: row.Notran },
                })
                .then(({ data }) => {
                    const payload = data.data || {};

                    this.viewer.head = payload.head || {};
                    this.viewer.groups =
                        (payload.detail || {}).arr_detail_pemeriksaan || {};
                    this.viewer.nilai_normal = payload.nilai_normal || {};
                    this.viewer.loading = false;
                })
                .catch(() => {
                    this.viewer = null;
                    this.$swal({
                        icon: "warning",
                        title: "Detail laboratorium tidak tersedia",
                        text: "Data gagal diambil dari api-simrs.",
                    });
                });
        },

        openRad(row) {
            this.viewer = {
                type: "rad",
                title: row.date,
                loading: true,
                rows: [],
            };

            this.axios
                .get("/api/riwayat-pasien/radiologi-detail", {
                    params: { regno: this.regnoAktif, notrans: row.NoTran },
                })
                .then(({ data }) => {
                    this.viewer.rows = data.data || [];
                    this.viewer.loading = false;
                })
                .catch(() => {
                    this.viewer = null;
                    this.$swal({
                        icon: "warning",
                        title: "Detail radiologi tidak tersedia",
                        text: "Data gagal diambil dari api-simrs.",
                    });
                });
        },

        nilaiNormal(row) {
            if (row.NilaiNormal) return row.NilaiNormal;

            const rujukan = (this.viewer.nilai_normal || {})[row.KDDetail];
            if (!rujukan) return "-";

            return (
                [rujukan.nilai_rentang_normal, rujukan.satuan_nilai]
                    .filter(Boolean)
                    .join(" ") || "-"
            );
        },

        isKritis(row) {
            return Number(row.IsNilaiKritisLab) === 1;
        },

        fotoSrc(foto) {
            const value = foto.Foto;
            if (!value || typeof value !== "string") return null;

            if (
                value.startsWith("data:") ||
                value.startsWith("http") ||
                value.startsWith("/")
            ) {
                return value;
            }

            return `data:image/jpeg;base64,${value}`;
        },

        dokumenUrl(url) {
            return (url || "").replace("/var/www/html", "");
        },

        fileBerkas(berkas) {
            return (berkas.berkas_rm_files || [])
                .filter((file) => file.file_path)
                .map((file, index) => ({
                    key: `file-${file.id}`,
                    url: file.file_path,
                    label: `Lembar ${index + 1}`,
                }));
        },

        openBerkas(berkas, index) {
            this.preview = {
                title: [berkas.doc_title, berkas.doc_description]
                    .filter(Boolean)
                    .join(" — "),
                files: this.fileBerkas(berkas),
                index,
            };
        },

        closeBerkas() {
            this.preview = null;
        },

        geserBerkas(step) {
            const total = this.preview.files.length;
            this.preview.index = (this.preview.index + step + total) % total;
        },

        onKeydownPreview(event) {
            if (!this.preview) return;

            if (event.key === "Escape") this.closeBerkas();
            if (event.key === "ArrowLeft") this.geserBerkas(-1);
            if (event.key === "ArrowRight") this.geserBerkas(1);
        },

        formatTanggal(value) {
            if (!value) return "-";
            return moment(String(value)).format("DD MMM YYYY");
        },

        formatTanggalJam(value) {
            if (!value) return "-";
            return moment(String(value)).format("DD MMM YYYY HH:mm");
        },

        formatTanggalCppt(value) {
            if (!value) return "-";
            return moment(String(value)).format("DD-MM-YYYY");
        },

        formatJamCppt(value) {
            if (!value) return "";
            return moment(String(value)).format("HH:mm:ss");
        },

        formatTanggalObat(value) {
            if (!value) return "";
            return moment(String(value)).format("DD-MM-YYYY");
        },

        formatTanggalJamObat(value) {
            if (!value) return "-";
            return moment(String(value)).format("DD-MM-YYYY HH:mm");
        },

        waktuResep(r) {
            const tanggal = this.formatTanggalObat(r.tanggal);
            const jam = r.jam ? moment(String(r.jam)).format("HH:mm") : "";

            return [tanggal, jam].filter(Boolean).join(" ") || "-";
        },
    },
};
</script>

<style scoped>
.snapshot-frame {
    width: 100%;
    height: 65vh;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    background: #fff;
}

.rad-foto {
    max-height: 220px;
}

.hasil-rad {
    white-space: pre-wrap;
}

.accordion-button:not(.collapsed) {
    background-color: #e8f5ee;
    color: #146c43;
}

.berkas-thumb {
    width: 120px;
}

.berkas-thumb img {
    width: 100%;
    height: 90px;
    object-fit: cover;
    background: #f8f9fa;
}

.berkas-preview {
    position: fixed;
    inset: 0;
    z-index: 1080;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 56px 56px 24px;
}

.berkas-preview-bar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    color: #fff;
    background: rgba(0, 0, 0, 0.5);
}

.berkas-preview-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    background: #fff;
}

.berkas-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    margin: 0 8px;
    border: 0;
    border-radius: 50%;
    color: #fff;
    background: rgba(255, 255, 255, 0.2);
}

.berkas-nav:hover {
    background: rgba(255, 255, 255, 0.35);
}

.cppt-table {
    font-size: 0.875rem;
    background: #fff;
}

.cppt-table thead th {
    vertical-align: middle;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.soap-kode {
    width: 34px;
    background: #f8f9fa;
}

.pre-wrap {
    white-space: pre-wrap;
}

.resep-baris {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px dashed #dee2e6;
}

.resep-baris:last-child {
    border-bottom: 0;
}

.resep-baris > div:nth-child(2) {
    flex: 1;
}

.resep-r {
    width: 32px;
    font-style: italic;
    font-size: 1.25rem;
    font-weight: 700;
}
</style>
